import { useSyncExternalStore } from "react";

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// "prompt": the browser's own install dialog; the others: Add to Home Screen by hand.
export type InstallMode = "prompt" | "ios" | "android" | "macos-safari";

export interface InstallSnapshot {
  installed: boolean;
  mode: InstallMode | null;
  invite: boolean;
}

type KeyValue = Pick<Storage, "getItem" | "setItem">;

export interface InstallEnv {
  target: EventTarget;
  matchMedia: (query: string) => { matches: boolean };
  navigator: { userAgent: string; maxTouchPoints?: number; standalone?: boolean };
  hasPromptEvent: boolean;
  session: KeyValue | null;
}

const DISMISSED_KEY = "install-invite-dismissed";
const DISPLAY_MODES = ["standalone", "fullscreen", "minimal-ui", "window-controls-overlay"];

export function isInstalledContext(env: Pick<InstallEnv, "matchMedia" | "navigator">): boolean {
  return (
    env.navigator.standalone === true ||
    DISPLAY_MODES.some((m) => env.matchMedia(`(display-mode: ${m})`).matches)
  );
}

// Browsers without beforeinstallprompt that can still add the app to the home screen by hand.
export function manualInstallMode(userAgent: string, maxTouchPoints = 0): InstallMode | null {
  const mac = /Macintosh/.test(userAgent);
  if (/iPhone|iPad|iPod/.test(userAgent) || (mac && maxTouchPoints > 1)) return "ios";
  if (/Android/.test(userAgent)) return "android";
  const safari =
    /Safari/.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS/.test(userAgent);
  const version = Number(/Version\/(\d+)/.exec(userAgent)?.[1] ?? 0);
  if (mac && safari && version >= 17) return "macos-safari";
  return null;
}

function read(store: KeyValue | null, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(store: KeyValue | null, key: string, value: string) {
  try {
    store?.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the dismissal lasts until the page is reloaded.
  }
}

// Dismissal is kept per browser session, so a later fresh visit invites again. Installation is not
// persisted: Chromium stops firing beforeinstallprompt while the app is installed.
export function createInstallController(env: InstallEnv) {
  let deferred: BeforeInstallPromptEvent | null = null;
  let installed = isInstalledContext(env);
  let dismissed = read(env.session, DISMISSED_KEY) === "1";
  const listeners = new Set<() => void>();

  const compute = (): InstallSnapshot => {
    const mode: InstallMode | null = installed
      ? null
      : deferred
        ? "prompt"
        : env.hasPromptEvent
          ? null
          : manualInstallMode(env.navigator.userAgent, env.navigator.maxTouchPoints);
    return { installed, mode, invite: mode !== null && !dismissed };
  };
  let snapshot = compute();
  const emit = () => {
    snapshot = compute();
    listeners.forEach((l) => l());
  };

  const dismiss = () => {
    dismissed = true;
    write(env.session, DISMISSED_KEY, "1");
    emit();
  };

  env.target.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  env.target.addEventListener("appinstalled", () => {
    deferred = null;
    installed = true;
    emit();
  });

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    dismiss,
    // The native dialog opens only from this call, i.e. after the employee taps install.
    install: async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
      const event = deferred;
      if (!event) return "unavailable";
      deferred = null;
      emit();
      await event.prompt();
      const { outcome } = await event.userChoice;
      if (outcome === "dismissed") dismiss();
      return outcome;
    },
  };
}

export type InstallController = ReturnType<typeof createInstallController>;

let controller: InstallController | null = null;

function sessionStore(): KeyValue | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

// Called from the entry module, so the early beforeinstallprompt event is never missed.
export function initInstall(): InstallController {
  controller ??= createInstallController({
    target: window,
    matchMedia: (q) => window.matchMedia(q),
    navigator: navigator as InstallEnv["navigator"],
    hasPromptEvent: "onbeforeinstallprompt" in window,
    session: sessionStore(),
  });
  return controller;
}

export function useInstall() {
  const c = initInstall();
  const snapshot = useSyncExternalStore(c.subscribe, c.getSnapshot);
  return { ...snapshot, install: c.install, dismiss: c.dismiss };
}
